import { useMemo, useState } from 'react';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction, SearchField } from '../../components/data-grid/RouteDataGrid';
import { fmtDateTime } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { AuthorizationListRow } from '../../api/types';
import { useAuthorizationsList, useMe } from '../../api/queries';
import { DecisionDialog } from './DecisionDialog';

export default function ApprovalsPage() {
  usePageTitle('Approvals');
  const me = useMe();
  const list = useAuthorizationsList();
  const [openRow, setOpenRow] = useState<AuthorizationListRow | null>(null);

  const myUserId = me.data?.user_id ?? null;

  const columns = useMemo<GridColDef<AuthorizationListRow>[]>(() => [
    {
      field: 'actionClass', headerName: 'Action', flex: 1.4,
      renderCell: (p) => (
        <Stack sx={{ py: 0.5 }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            {p.row.actionClass}
          </Typography>
          {p.row.targetResourceType && (
            <Typography variant="caption" color="text.secondary">
              {p.row.targetResourceType} {p.row.targetResourceId ?? ''}
            </Typography>
          )}
        </Stack>
      ),
    },
    { field: 'state', headerName: 'State', width: 130,
      renderCell: (p) => <StateChip state={p.row.state} /> },
    { field: 'requesterUserId', headerName: 'Requester', width: 200,
      renderCell: (p) => (
        <Tooltip title={p.row.requesterUserId}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
            {p.row.requesterUserId === myUserId ? 'me' : `${p.row.requesterUserId.slice(0, 8)}…`}
          </Typography>
        </Tooltip>
      ),
    },
    { field: 'operatorId', headerName: 'Operator', width: 110,
      renderCell: (p) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
          {p.row.operatorId ? `${p.row.operatorId.slice(0, 8)}…` : 'ARMS'}
        </Typography>
      ),
    },
    { field: 'submittedAt', headerName: 'Submitted', width: 180,
      valueFormatter: (value) => fmtDateTime(value) },
    { field: 'expiresAt', headerName: 'Expires / Resolved', width: 180,
      renderCell: (p) => {
        const v = p.row.state === 'pending' ? p.row.expiresAt : p.row.resolvedAt;
        return <Typography variant="caption">{fmtDateTime(v)}</Typography>;
      },
    },
  ], [myUserId]);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text', key: 'actionClass', label: 'Action class' },
    { kind: 'text', key: 'requesterUserId', label: 'Requester user ID' },
    { kind: 'select', key: 'state', label: 'State', options: [
      { value: 'pending',   label: 'Pending' },
      { value: 'approved',  label: 'Approved' },
      { value: 'committed', label: 'Committed' },
      { value: 'rejected',  label: 'Rejected' },
      { value: 'cancelled', label: 'Cancelled' },
      { value: 'expired',   label: 'Expired' },
    ] },
  ], []);

  const quickFilters = useMemo<QuickFilter<AuthorizationListRow>[]>(() => [
    { label: 'Pending',   filter: (r) => r.state === 'pending' },
    { label: 'Mine',      filter: (r) => r.requesterUserId === myUserId },
    { label: 'Resolved',  filter: (r) => r.state !== 'pending' },
    { label: 'All',       filter: () => true },
  ], [myUserId]);

  const exportColumns = useMemo<ExportColumn<AuthorizationListRow>[]>(() => [
    { key: 'actionClass',         label: 'Action class' },
    { key: 'state',               label: 'State' },
    { key: 'requesterUserId',     label: 'Requester' },
    { key: 'operatorId',          label: 'Operator' },
    { key: 'targetResourceType',  label: 'Target type' },
    { key: 'targetResourceId',    label: 'Target ID' },
    { key: 'submittedAt',         label: 'Submitted', format: (v) => fmtDateTime(v) },
    { key: 'expiresAt',           label: 'Expires',   format: (v) => fmtDateTime(v) },
    { key: 'resolvedAt',          label: 'Resolved',  format: (v) => fmtDateTime(v) },
    { key: 'idempotencyKey',      label: 'Idempotency key' },
  ], []);

  const actions = useMemo<RowAction<AuthorizationListRow>[]>(() => [
    { label: 'View / decide', onClick: (row) => setOpenRow(row) },
  ], []);

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="h4">Authorization requests</Typography>
        <Typography variant="body2" color="text.secondary">
          UAC §12 — N-of-M approvals across the platform. Approve or reject
          requests where you hold one of the required roles. Eligibility is
          server-enforced; ineligible attempts are rejected.
        </Typography>

        <RouteDataGrid<AuthorizationListRow>
          entityName="Authorizations"
          queryKey={list.queryKey}
          listFn={list.listFn}
          columns={columns}
          searchFields={searchFields}
          quickFilters={quickFilters}
          exportColumns={exportColumns}
          reportTitle="ARMS — Authorization requests"
          actions={actions}
          onRowClick={(row) => setOpenRow(row)}
          emptyMessage="No authorization requests."
        />

        <DecisionDialog
          request={openRow}
          onClose={() => setOpenRow(null)}
        />
      </Stack>
    </>
  );
}

function StateChip({ state }: { state: string }) {
  const color =
    state === 'approved' || state === 'committed' ? 'success' :
    state === 'pending' ? 'warning' :
    state === 'rejected' ? 'error' :
    state === 'cancelled' || state === 'expired' ? 'default' : 'default';
  return <Chip size="small" label={state} color={color as 'success' | 'warning' | 'error' | 'default'} />;
}
